import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { JobsPanelState, JobsStatus, jobStatusToString } from "../../../constants/Jobs";
import { FeatureTableContext, JobContext, UserContext, VersionContext } from "../../../Context";
import {
    createNewJobStep,
    getJobRelatedDocumentation,
    getJobRelatedObjects,
    hideDocumentationPoints,
    setFieldSistKontrolSkjermingDate,
    showDocumentationPoints,
    updateJobStepComment,
} from "../../../utils/jobsUtils";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import InputField from "../../genericComponents/InputField/InputField";
import "./JobDetails.css";

import CheckPinIcon from "../../../assets/jobs/checkPin.svg?react";

import { formatDate } from "@arcgis/core/intl";
import { StoreState } from "../../../store/rootReducer";
import { getLayerName, getMapView } from "../../../utils/arcgisUtils";
import { FeatureWithChanges, findFeaturesWithChangesFromJob } from "../../../utils/newVersioningUtils";
import EditedFeaturesForOperativeCompare from "../EditedFeatures/EditedFeaturesForOperativeCompare";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import Checkbox from "../../genericComponents/Checkbox/Checkbox";
import { AppConfig } from "../../../AppConfig";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { triggerAlertsRefresh } from "../../../store/triggersSlice";
import { toast } from "react-toastify";
import { isDefaultVersion } from "../../../utils/versioningUtils";
import type Graphic from "@arcgis/core/Graphic";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface JobAnalysisCompletedProps {
    jobObject: Graphic;
    setLoading: (loading: boolean) => void;
    storeHighlightHandle: (handle: ResourceHandle | null) => void;
    setJobPanelState: (state: JobsPanelState) => void;
    clearHighlightedFeatures: () => void;
}

const JobAnalysisCompleted: React.FC<JobAnalysisCompletedProps> = ({
    jobObject,
    setJobPanelState,
    storeHighlightHandle,
    clearHighlightedFeatures,
    setLoading,
}) => {
    const dispatch = useDispatch();

    const [currentJobStep, setCurrentJobStep] = useState<Graphic | null>(null);
    const [historicalSteps, setHistoricalSteps] = useState<Graphic[]>([]);

    const [jobFeaturesWithChanges, setJobFeaturesWithChanges] = useState<FeatureWithChanges[]>([]);
    const [isLoadingChanges, setIsLoadingChanges] = useState<boolean>(true);

    const [documentation, setDocumentation] = useState<Graphic[]>([]);
    const [selectedDocumentation, setSelectedDocumentation] = useState<Graphic | null>(null);

    const [omraadeVisible, setOmraadeVisible] = useState(false);
    const [punkterVisible, setPunkterVisible] = useState(false);
    const [linjerVisible, setLinjerVisible] = useState(false);

    const jobsContext = useContext(JobContext);

    const userContext = useContext(UserContext);
    const user = userContext?.user.value;

    const versionContext = useContext(VersionContext);
    const versionManagementRef = versionContext?.versionServiceRef.value;

    const [comment, setComment] = useState("");

    const returnToJobList = () => {
        setJobPanelState(JobsPanelState.LIST);
        jobsContext?.selectedJob.set(null);
    };

    const checkIfDefaultVersion = () => {
        if (versionManagementRef?.current && activeVersion) {
            return isDefaultVersion(activeVersion, versionManagementRef.current);
        }
    };

    /**
     * Adds new step to job with status KontrollskjermingAvvik and comment from input, which indicates that there was an avvik with the analysis or the lighthouse.
     * This allows the workflow to be improved by providing feedback on what went wrong with the analysis.
     */
    const handleAvvik = async () => {
        try {
            setLoading(true);
            if (jobObject) {
                const success = await updateJobStepComment(
                    currentJobStep?.attributes.globalid,
                    comment ?? "",
                    user?.username ?? ""
                );
                if (success) {
                    const newJobStatus = JobsStatus.KontrollskjermingAvvik;
                    const jobStepGlobalId = await createNewJobStep(jobObject.attributes.globalid, newJobStatus);
                    if (jobStepGlobalId) {
                        dispatch(triggerAlertsRefresh());
                        returnToJobList();
                    }
                }
            }
        } catch (error) {
            console.error("Error marking job as KontrollskjermingAvvik:", error);
            toast.error("En feil oppsto ved markering av droneskjerming som avvik.");
        } finally {
            setLoading(false);
        }
    };

    const handleGodkjent = async () => {
        try {
            setLoading(true);
            if (jobObject) {
                const successCommentUpdate = await updateJobStepComment(
                    currentJobStep?.attributes.globalid,
                    comment ?? "",
                    user?.username ?? ""
                );
                const successFeatureUpdate = await setFieldSistKontrolSkjermingDate(
                    jobFeaturesWithChanges[0]?.mainFeature.versionGraphic!
                );
                if (successCommentUpdate && successFeatureUpdate) {
                    const newJobStatus = JobsStatus.Ferdig;
                    const jobStepGlobalId = await createNewJobStep(jobObject.attributes.globalid, newJobStatus);
                    if (jobStepGlobalId) {
                        dispatch(triggerAlertsRefresh());
                        returnToJobList();
                    }
                }
            }
        } catch (error) {
            console.error("Error marking job as Ferdig:", error);
            toast.error("En feil oppsto ved markering av droneskjerming som godkjent.");
        } finally {
            setLoading(false);
        }
    };

    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const featureTableContext = useContext(FeatureTableContext);

    const getObjectsConnectedToJob = async () => {
        const job = jobObject;
        const versionName = activeVersion?.versionIdentifier.name;

        if (job && versionName) {
            setIsLoadingChanges(true);
            const objects = await findFeaturesWithChangesFromJob(job, versionName);
            setJobFeaturesWithChanges(objects);
            setIsLoadingChanges(false);
        }
    };

    const getDocumentationSeamarkNumber = (graphic: Graphic) => {
        const documentationRelatedObjectGlobalId = graphic.attributes.nautisk_objekt_globalid;
        const documentationRelatedLayerId = graphic.attributes.nautisk_objekt_kilde;
        let documentationRelatedFeature: Graphic | undefined;
        for (let featureWithChange of jobFeaturesWithChanges) {
            if (
                featureWithChange.mainFeature.layerId === documentationRelatedLayerId &&
                featureWithChange.mainFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
            ) {
                documentationRelatedFeature = featureWithChange.mainFeature.versionGraphic;
                break;
            }
            for (let relatedFeature of featureWithChange.relatedFeatures) {
                if (
                    relatedFeature.layerId === documentationRelatedLayerId &&
                    relatedFeature.versionGraphic?.attributes.globalid === documentationRelatedObjectGlobalId
                ) {
                    documentationRelatedFeature = relatedFeature.versionGraphic;
                    break;
                }
            }
            if (documentationRelatedFeature) {
                break;
            }
        }

        return (
            documentationRelatedFeature?.attributes["sjomerke_nr"] ??
            documentationRelatedFeature?.attributes["navn"] ??
            documentationRelatedFeature?.attributes["objectid"] + getLayerName(documentationRelatedLayerId)
        );
    };

    const setResultLayerVisibility = (title: string, layerId: number, checked: boolean) => {
        const mapview = getMapView();
        if (!mapview?.map) return;

        const existingLayer = mapview.map.layers.find((layer) => layer.title === title);
        if (existingLayer) {
            existingLayer.visible = checked;
            return;
        }
        if (!AppConfig.FeatureToggles.DroneControlJobs) return;

        const newLayer = new FeatureLayer({
            title,
            layerId,
            visible: checked,
            minScale: 200000,
            maxScale: 0,
            definitionExpression: "job_id = '" + jobObject.attributes.globalid + "'",
            portalItem: {
                id: AppConfig.droneResultsLayerPortalItemId,
                portal: {
                    url: AppConfig.ArcGIS.BaseUrl,
                },
            },
        });
        mapview.map.add(newLayer);
    };

    const toggleOmraadeVisibility = (checked: boolean) => setResultLayerVisibility("Resultat Område", 0, checked);
    const togglePunkterVisibility = (checked: boolean) => setResultLayerVisibility("Resultat Punkter", 1, checked);
    const toggleLinjerVisibility = (checked: boolean) => setResultLayerVisibility("Resultat Linjer", 2, checked);

    useEffect(() => {
        getJobRelatedObjects(jobObject.attributes.objectid, "Steg").then((objects) => {
            // Find step before this step
            const orderedSteps = [...objects];
            // Sorts steps by opprettet_dato in descending order in case there are multiple steps with the same jobb_status
            orderedSteps.sort((a, b) => b.attributes.opprettet_dato - a.attributes.opprettet_dato);
            let activeStep;
            for (let step of orderedSteps) {
                if (step.attributes.jobb_status === jobObject.attributes.status) {
                    activeStep = step;
                    break;
                }
            }
            if (activeStep) {
                setCurrentJobStep(activeStep);
                setHistoricalSteps(() => {
                    if (jobObject.attributes.status === JobsStatus.Ferdig) {
                        return orderedSteps;
                    } else {
                        return orderedSteps.filter(
                            (step) => step.attributes.globalid !== activeStep.attributes.globalid
                        );
                    }
                });
            }
        });
        getJobRelatedDocumentation(jobObject.attributes.objectid).then((docs) => {
            setDocumentation(docs);
        });
    }, [jobObject]);

    useEffect(() => {
        if (selectedDocumentation) {
            showDocumentationPoints(selectedDocumentation).then((layer) => {
                if (layer) {
                    if (!layer.loaded) {
                        layer.load().then(() => {
                            featureTableContext?.isOpen.set(true);
                            featureTableContext?.activeLayer.set(layer);
                        });
                    } else {
                        featureTableContext?.isOpen.set(true);
                        featureTableContext?.activeLayer.set(layer);
                    }
                }
            });
        }
    }, [selectedDocumentation]);

    useEffect(() => {
        getObjectsConnectedToJob();

        return () => {
            // remove result layers from map when unmounting
            const mapview = getMapView();
            if (!mapview?.map) return;
            const layersToRemove = mapview.map.layers.filter((layer) =>
                ["Resultat Område", "Resultat Punkter", "Resultat Linjer"].includes(layer.title ?? "")
            );
            layersToRemove.forEach((layer) => mapview.map?.remove(layer));
        };
    }, []);

    useEffect(() => {
        const map = getMapView()?.map;
        if (!map) return;

        const handles = [
            reactiveUtils.watch(
                () => map.layers.find((layer) => layer.title === "Resultat Område")?.visible ?? false,
                (value) => setOmraadeVisible(value),
                { initial: true }
            ),
            reactiveUtils.watch(
                () => map.layers.find((layer) => layer.title === "Resultat Punkter")?.visible ?? false,
                (value) => setPunkterVisible(value),
                { initial: true }
            ),
            reactiveUtils.watch(
                () => map.layers.find((layer) => layer.title === "Resultat Linjer")?.visible ?? false,
                (value) => setLinjerVisible(value),
                { initial: true }
            ),
        ];

        return () => handles.forEach((h) => h.remove());
    }, []);

    useEffect(() => {
        return () => {
            featureTableContext?.activeLayer.set(undefined);
            featureTableContext?.isOpen.set(false);
            hideDocumentationPoints();
        };
    }, []);

    return (
        <div className="job-details-body">
            <div className="job-details-body-content">
                <label className="job-details-label">
                    <span className="hidden">{jobObject.attributes.globalid}</span>
                    <span>Jobbnavn</span>
                    <span>{jobObject.attributes.navn}</span>
                </label>
                <label className="job-details-label">
                    <span>Beskrivelse</span>
                    <span>{jobObject.attributes.beskrivelse}</span>
                </label>
                {historicalSteps.length > 0 && (
                    <Collapsible
                        isOpen={true}
                        setIsOpen={() => {}}
                        title={`Steg med kommentarer (${historicalSteps.length})`}
                        titleClassName="input-field-label"
                    >
                        {historicalSteps.map((step) => (
                            <label key={step.attributes.globalid} className="job-step-container">
                                <div className="job-step-header">
                                    <span>
                                        <strong>{jobStatusToString(step.attributes.jobb_status)}</strong>
                                    </span>
                                    <span>
                                        {step.attributes.fullfort_av} ({formatDate(step.attributes.oppdatert_dato)})
                                    </span>
                                </div>
                                <span>{step.attributes.kommentar ?? "[ingen kommentar]"}</span>
                            </label>
                        ))}
                    </Collapsible>
                )}

                <div className="empty-button flex-row small-gap center">
                    <CheckPinIcon />
                    {`Objekter tilknyttet jobben (${jobFeaturesWithChanges.length})`}
                </div>
                <div className="flex-row space-between">
                    <span className="job-card-key">Eier</span>
                    <span className="job-card-value bold">{jobObject.attributes.opprettet_av}</span>
                </div>
                <div className="flex-row space-between">
                    <span className="job-card-key">Oppdatert</span>
                    <span className="job-card-value bold">{formatDate(jobObject.attributes.oppdatert_dato)}</span>
                </div>
                {documentation.length > 0 && (
                    <Collapsible
                        isOpen={false}
                        setIsOpen={() => {}}
                        title={`Dokumentasjon (${documentation.length})`}
                        titleClassName="input-field-label"
                    >
                        {documentation.map((doc) => {
                            return (
                                <div key={doc.attributes.globalid} className="job-details-documentation-item">
                                    <NfsButton
                                        outlined
                                        className={`w-100 job-details-documentation-button ${
                                            doc === selectedDocumentation ? "selected-documentation" : ""
                                        }`}
                                        onClick={() => {
                                            setSelectedDocumentation(doc);
                                        }}
                                    >
                                        <span>
                                            {getDocumentationSeamarkNumber(doc) +
                                                " " +
                                                (doc.attributes.forklaring ?? doc.attributes.beskrivelse ?? "")}
                                        </span>
                                    </NfsButton>
                                </div>
                            );
                        })}
                    </Collapsible>
                )}
            </div>
            <div>
                <span>Her kan du styre synligheten til resultatet fra droneskjermingen.</span>
                <div className="flex-row small-gap">
                    <label htmlFor={"bbox"} className="center flex-row small-gap">
                        Område
                        <Checkbox checked={omraadeVisible} id={"bbox"} onChange={toggleOmraadeVisibility} />
                    </label>
                    <label htmlFor={"points"} className="center flex-row small-gap">
                        Punkter
                        <Checkbox checked={punkterVisible} id={"points"} onChange={togglePunkterVisibility} />
                    </label>
                    <label htmlFor={"lines"} className="center flex-row small-gap">
                        Linjer
                        <Checkbox checked={linjerVisible} id={"lines"} onChange={toggleLinjerVisibility} />
                    </label>
                </div>
            </div>
            <EditedFeaturesForOperativeCompare
                featuresAddedToJob={jobFeaturesWithChanges}
                isLoadingChanges={isLoadingChanges}
                storeHighlightHandle={storeHighlightHandle}
                clearHighlightedFeatures={clearHighlightedFeatures}
                jobStatus={jobObject.attributes.status}
            />
            {checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    {jobObject.attributes.status === JobsStatus.Ferdig && (
                        <span>
                            Dersom skjermingen ikke er god nok kan den flagges som avvik slik at prosesseringen kan
                            forbedres og manuellskjerming kan utføres
                        </span>
                    )}
                    {jobObject.attributes.status === JobsStatus.KontrollskjermingAvvik && (
                        <span>Dersom skjermingen er god nok kan den markeres som godkjent</span>
                    )}
                    <InputField
                        label="Kommentar"
                        onChange={setComment}
                        inputFieldId={jobObject.attributes.globalid + "comment"}
                        value={comment}
                        type="text"
                        isTextArea
                        textAreaRows={3}
                        maxLength={255}
                    />
                    <div className="job-details-actions-buttons">
                        {jobObject.attributes.status === JobsStatus.Ferdig && (
                            <NfsButton outlined disabled={isLoadingChanges} onClick={handleAvvik}>
                                Avvik
                            </NfsButton>
                        )}
                        {jobObject.attributes.status === JobsStatus.KontrollskjermingAvvik && (
                            <NfsButton disabled={isLoadingChanges} onClick={handleGodkjent}>
                                Godkjent
                            </NfsButton>
                        )}
                    </div>
                </div>
            )}
            {!checkIfDefaultVersion() && (
                <div className="job-details-actions">
                    <span>For å sikre datakvalitet kan kontrollskjermingsresultat kun overstyres fra Planroot</span>
                </div>
            )}
        </div>
    );
};

export default JobAnalysisCompleted;
